/**
 * Composant StarRating pour afficher et saisir des notes etoiles
 * Style Amazon
 */

import React from 'react';

interface StarRatingProps {
  rating: number; // Note moyenne (0-5)
  totalReviews?: number; // Nombre d'avis (optionnel)
  size?: 'small' | 'medium' | 'large';
  showCount?: boolean; // Afficher le nombre d'avis
  showRating?: boolean; // Afficher la note numerique
  interactive?: boolean; // Permettre de cliquer pour noter
  onRatingChange?: (rating: number) => void; // Callback quand on clique
  className?: string;
}

export function StarRating({
  rating,
  totalReviews = 0,
  size = 'medium',
  showCount = true,
  showRating = false,
  interactive = false,
  onRatingChange,
  className = '',
}: StarRatingProps) {
  const [hoverRating, setHoverRating] = React.useState(0);

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { starSize: 14, fontSize: '0.75rem', gap: '2px' };
      case 'large':
        return { starSize: 24, fontSize: '1rem', gap: '4px' };
      default:
        return { starSize: 18, fontSize: '0.85rem', gap: '3px' };
    }
  };

  const { starSize, fontSize, gap } = getSizeStyles();

  const handleClick = (starIndex: number) => {
    if (interactive && onRatingChange) {
      onRatingChange(starIndex);
    }
  };

  const handleMouseEnter = (starIndex: number) => {
    if (interactive) {
      setHoverRating(starIndex);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  const displayRating = hoverRating > 0 ? hoverRating : rating;

  const renderStar = (index: number) => {
    const fillPercentage = Math.min(Math.max(displayRating - index, 0), 1) * 100;
    const isFull = fillPercentage >= 100;
    const isPartial = fillPercentage > 0 && fillPercentage < 100;
    const isEmpty = fillPercentage === 0;

    return (
      <span
        key={index}
        style={{
          position: 'relative',
          display: 'inline-block',
          width: starSize,
          height: starSize,
          cursor: interactive ? 'pointer' : 'default',
        }}
        onClick={() => handleClick(index + 1)}
        onMouseEnter={() => handleMouseEnter(index + 1)}
        onMouseLeave={handleMouseLeave}
      >
        {/* Etoile vide (fond) */}
        <svg
          width={starSize}
          height={starSize}
          viewBox="0 0 24 24"
          fill="none"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill={isEmpty ? '#D1D5DB' : 'none'}
            stroke="#D1D5DB"
            strokeWidth="1"
          />
        </svg>

        {/* Etoile pleine ou partielle (devant) */}
        {!isEmpty && (
          <svg
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              clipPath: isPartial ? `inset(0 ${100 - fillPercentage}% 0 0)` : undefined,
            }}
          >
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="#F59E0B"
              stroke="#F59E0B"
              strokeWidth="1"
            />
          </svg>
        )}
      </span>
    );
  };

  return (
    <div
      className={`star-rating ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: gap,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1px',
        }}
      >
        {[0, 1, 2, 3, 4].map(renderStar)}
      </div>

      {showRating && (
        <span
          style={{
            fontSize,
            fontWeight: 600,
            color: '#F59E0B',
            marginLeft: '4px',
          }}
        >
          {rating.toFixed(1)}
        </span>
      )}

      {showCount && totalReviews > 0 && (
        <span
          style={{
            fontSize,
            color: '#007185',
            marginLeft: '4px',
          }}
        >
          ({totalReviews.toLocaleString('fr-FR')})
        </span>
      )}

      {showCount && totalReviews === 0 && !interactive && (
        <span
          style={{
            fontSize,
            color: '#6B7280',
            marginLeft: '4px',
          }}
        >
          (Aucun avis)
        </span>
      )}
    </div>
  );
}

/**
 * Composant pour afficher la distribution des notes (comme Amazon)
 */
interface RatingDistributionProps {
  distribution: {
    '5': number;
    '4': number;
    '3': number;
    '2': number;
    '1': number;
  };
  total: number;
  onFilterClick?: (rating: number) => void;
}

export function RatingDistribution({ distribution, total, onFilterClick }: RatingDistributionProps) {
  const getPercentage = (count: number) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  return (
    <div className="rating-distribution" style={{ width: '100%' }}>
      {[5, 4, 3, 2, 1].map((stars) => {
        const count = distribution[String(stars) as keyof typeof distribution];
        const percentage = getPercentage(count);

        return (
          <div
            key={stars}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px',
              cursor: onFilterClick ? 'pointer' : 'default',
            }}
            onClick={() => onFilterClick?.(stars)}
          >
            <span
              style={{
                fontSize: '0.85rem',
                color: '#007185',
                width: '60px',
                whiteSpace: 'nowrap',
              }}
            >
              {stars} etoile{stars > 1 ? 's' : ''}
            </span>
            <div
              style={{
                flex: 1,
                height: '16px',
                backgroundColor: '#E5E7EB',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${percentage}%`,
                  backgroundColor: '#F59E0B',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span
              style={{
                fontSize: '0.85rem',
                color: '#007185',
                width: '40px',
                textAlign: 'right',
              }}
            >
              {percentage}%
            </span>
          </div>
        );
      })}
    </div>
  );
}
